import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as applicationActions from '../../../redux/actions/ApplicationsActions';
import { printChecks } from '../../../redux/actions/CheckPrintingActions';
import { toggleSidebar, resetFilters } from '../../../redux/actions/SidebarFilterActions';
import { Departments, PermissionTypes } from '../../../lib/systemVariables';
import { TableNames, TableViews } from '../../../lib/constants';
import { getQueryParams, setQueryParams } from '../../../lib/urlUtils';
import { getRequest } from '../../../lib/apiUtils';
import { debounce } from 'throttle-debounce';

import ApplicationsPageActionsView from './ApplicationsPageActionsView';
import ApplicationTable from './ApplicationsTable';
import SidebarFilter from '../../commonComponents/shared/SidebarFilter';
import ApplicationsListControls from './ApplicationsListControls';
import ApplicationsReportModal from './ApplicationsReportModal';
import ACHResultsPage from '../../achComponents/ACHResultsPage';

class ApplicationsPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            search: '',
            reportModal: false,
            showACHResults: false,
            achResults: []
        };

        this.filterApplications = this.filterApplications.bind(this);
    }

    componentDidMount() {
        if (getQueryParams().applyFilters) {
            const tableView = getQueryParams().view;
            if (tableView) {
                this.toggleView(tableView, true);
            }
            else {
                this.setState({search: this.props.filters.elasticSearchText || ''}, () => {
                    this.props.actions.getApplications(
                        this.state.search,
                        this.props.applicationsTable.page,
                        this.props.applicationsTable.resultsView,
                        this.props.applicationsTable.sort);
                })
            }
        } 
        else { //manually rest and reload data, only if not popped off stack
                this.props.actions.resetFilters(TableNames.Applications, true); //reset and apply filters
                this.props.actions.filterApplications(true); //reset table and reload applications and counts
        }

        this.props.actions.getStatusCounts();
    }

    componentWillUnmount() {
        if (this.props.applicationsTable.selected.length) {
            this.props.actions.clearApplicationsSelection();
        }
    }

    search = e => {
        this.setState({search: e.target.value});
        if (!getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
        this.searchDebounce();
    }

    searchDebounce = debounce(400, () => {
        this.props.actions.filterApplications(
            true, 
            {
                 ...this.props.filters,
                days: { ...this.props.filters.days, Recent: "" }
            }, 
            this.state.search, 
            this.props.applicationsTable.resultsView, 
            this.state.search.length > 0 ? { by: 'ApplicationIds', direction: 'Ascending' } : undefined
         );
    });

    getApplications = (...props) => {
        this.props.actions.getApplications(this.state.search, ...props);
        if (props && !getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
    }

    filterApplications(updatedFilters, updateCounts, resetSort) {
        let tableSort = this.props.applicationsTable.sort;
    
        if (updatedFilters.lastName) {
            tableSort = { by: "lastNameDiff", direction: "Ascending" }
        } else if (updatedFilters.firstName) {
            tableSort = { by: "firstNameDiff", direction: "Ascending" }
        } else if (updatedFilters.status ===  null && 
                   (this.props.applicationsTable.sort.by === "lastNameDiff" ||
                    this.props.applicationsTable.sort.by === "firstNameDiff")) {
            tableSort = undefined;
        }

        this.props.actions.filterApplications(updateCounts, updatedFilters, this.state.search, this.props.applicationsTable.resultsView, resetSort ? undefined : tableSort);
        if (updatedFilters && !getQueryParams().applyFilters) {
            setQueryParams({ applyFilters: true });
        }
    }

    searchApplications = (updatedFilters) => {
        this.filterApplications({
            ...updatedFilters,
            status: null,
            days: { ...this.props.filters.days, Recent: "" },
            //reset table status to All & All time.
        }, true);
    }

    toggleView = view => {
        if (view !== this.props.filters.view) {
          const sortBy = this.props.applicationsTable.sort.by;
          const resetSort =
            view === TableViews.Standard
              ? sortBy === "address" ||
                sortBy === "teudatZahut" ||
                sortBy === "totalPaid" ||
                sortBy === "source" ||
                sortBy === "note" ||
                sortBy === "references"
              : sortBy === "fund";
          this.filterApplications({ view }, false, resetSort);
          setQueryParams({ view: view === TableViews.Standard ? null : view });
        }
    }

    handleACHResultsReady = (achResults) => {
        this.setState({ 
            showACHResults: true, 
            achResults: achResults 
        });
    }

    closeACHResults = () => {
        this.setState({ 
            showACHResults: false, 
            achResults: [] 
        });
        this.props.actions.clearApplicationsSelection();
        this.filterApplications({}, true);
    }

    toggleReportModal = () => {
        this.setState(prevState => ({ reportModal: !prevState.reportModal }));
    }

    getApplicationsForExport = async (params) => {
        const filterSettings = this.props.filters;

        for (var filterProperty in filterSettings) {
            if (filterProperty !== "days" && filterProperty !== "status") {
                let filterVal = filterSettings[filterProperty];

                if (filterProperty === "recipients") {
                    filterVal = filterSettings.recipients.map(rec => rec.applicantId);
                }
                params[filterProperty] = filterVal;
            }
        }

        return await getRequest("/applications/report", params, null, { responseType: 'blob' });
    }

    render() {
        if (this.state.showACHResults) {
            return (
                <ACHResultsPage 
                    achResults={this.state.achResults}
                    onClose={this.closeACHResults}
                />
            );
        }

        return (
            <div className={`page page-with-sidebar applications ${!this.props.sidebarVisible ? "page-with-sidebar-hidden" : ""}`}>
                <SidebarFilter type={TableNames.Applications} search={this.searchApplications} />
                <div className="subheader relative">
                    <div className="flex-justify flex-center">
                        <div className="flex-one">
                            <i className="material-icons sidebar-slide-out" onClick={() => this.props.actions.toggleSidebar(TableNames.Applications)}>filter_list</i>
                            <h2 className="page-header">Applications</h2>
                        </div>
                        <div className="flex-one">
                            <div className="elastic-search">
                                <div className="search-input">
                                    <i className="material-icons">search</i>
                                    <input type="text" name="search" placeholder="Search (Max Results: 100)" value={this.state.search || ''} onChange={e => this.search(e)} />
                                </div>
                            </div>
                        </div>
                        <ApplicationsPageActionsView
                            canCreate={this.props.userPermissions.manage}
                            userPermissions={this.props.userPermissions}
                            userProfile={this.props.userProfile}
                            filters={this.props.filters}
                            sort={this.props.applicationsTable.sort}
                            selected={this.props.applicationsTable.selected}
                            selectedCounts={this.props.selectedCounts}
                            funds={this.props.funds}
                            inquireOnly={this.props.filters.inquireOnly}
                            noData={!this.props.applicationsTable.data.records || !this.props.applicationsTable.data.records.length}
                            printing={this.props.checkPrinting}
                            approving={this.props.updating}
                            sendingACH={this.props.sendingACH}
                            onACHResultsReady={this.handleACHResultsReady}
                            actions={{
                                toggleInquireOnly: () => this.filterApplications({ inquireOnly: !this.props.filters.inquireOnly }, true),
                                approveApplications: applications => this.props.actions.updateApplicationsStatus('approve', applications),
                                printChecks: this.props.actions.printChecks,
                                wireApplications: this.props.actions.wireApplications,
                                sendACHApplications: this.props.actions.sendACHApplications,
                                updateSelectedCounts: this.props.actions.updateSelectedCounts,
                                onActionCompleted: (keepSelect = []) => {
                                    this.filterApplications({}, true);
                                    keepSelect.forEach(app => this.props.actions.toggleSelectApplication(app));
                                },
                                toggleView: this.toggleView
                            }} />
                    </div>

                    <ApplicationsListControls
                        status={this.props.filters.status}
                        days={this.props.filters.days}
                        statusCounts={this.props.statusCounts.data}
                        inquireOnly={this.props.filters.inquireOnly}
                        toggleInquireOnly={() => this.filterApplications({ inquireOnly: !this.props.filters.inquireOnly }, true)}
                        update={(propertyName, value) => this.filterApplications({ [propertyName]: value }, propertyName === "days")}
                        filters={this.props.filters}
                        toggleView={this.toggleView}
                        canExport={this.props.userPermissions.report}
                        exportDisabled={!this.props.applicationsTable.data.records || !this.props.applicationsTable.data.records.length}
                        onExport={this.toggleReportModal} />

                    <ApplicationsReportModal
                        getDataForExport={this.getApplicationsForExport}
                        show={this.state.reportModal}
                        close={this.toggleReportModal}
                    />

                </div>
                <div className="container">
                    <ApplicationTable
                        {...this.props.applicationsTable}
                        view={this.props.filters.view}
                        isUSUser={this.props.userProfile.departments && this.props.userProfile.departments.indexOf(Departments.US) >= 0}
                        userPermissions={this.props.userPermissions}
                        funds={this.props.funds}
                        status={this.props.filters.status}
                        updateTable={this.props.actions.updateTable}
                        getApplications={this.getApplications}
                        selectApplication={this.props.actions.toggleSelectApplication}
                        updateApplication={this.props.actions.updateApplication}
                        deleteApplication={this.props.actions.deleteApplication} />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state, ownProps) {
    return {
        ...state.applications,
        applicationsTable: state.applicationsTable,
        sidebarVisible: state.applicationsSidebarFilters.showSidebar,
        checkPrinting: state.checkPrinting.printing,
        selectedCounts: state.applications.selectedCounts,
        userProfile: state.userProfile.profile.data,
        userPermissions: {
            manage: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsManage) >= 0,
            approveFull: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsApproveFull) >= 0,
            approveLimited: state.userProfile.permissions.data.indexOf(PermissionTypes.ApplicationsApproveLimited) >= 0,
            wire: state.userProfile.permissions.data.indexOf(PermissionTypes.PaymentsWire) >= 0,
            print: state.userProfile.permissions.data.indexOf(PermissionTypes.PaymentsPrint) >= 0,
            report: state.userProfile.permissions.data.indexOf(PermissionTypes.Reporting) >= 0
        },
        funds: state.funds.fundsByDept.allWithLimits
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({ ...applicationActions, printChecks, toggleSidebar, resetFilters }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationsPage);


// WEBPACK FOOTER //
// ./src/components/applicationsComponents/applications/ApplicationsPage.jsx